import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import WhatsAppNotice from '../../components/common/WhatsAppNotice';
import EventCta from '../../components/common/EventCta';
import { apiRequest, handleBranding, formatDate, applyStoredBrandingColors } from '../../utils';

/* Public accept page (/invite/:token). A company's contact person shared a per-Lab invite
   link; accepting claims one of the company's open seats for that Lab, links the person to the
   company (employee + Bravos member), and signs them into the member home.
   Backend: GET /api/bravos/invite/:token + POST /api/bravos/invite/:token/accept. */
const InviteAccept = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  // Set when the typed email already has an account: the shareable link can't prove it's
  // theirs, so they log in first and reopen the link (then it claims onto that session).
  const [loginFirst, setLoginFirst] = useState(false);

  useEffect(() => {
    (async () => {
      // Resolve default branding first, THEN override with the invite's own BU brand (colour +
      // background) so a fresh invitee lands on the Bravos look, not Tornea's. Await handleBranding
      // so it can't clobber our override afterwards.
      await handleBranding();
      try {
        const data = await apiRequest('GET', `/bravos/invite/${token}`);
        setInfo(data);
        if (data && data.valid) {
          if (data.primary_color) localStorage.setItem('primaryColor', data.primary_color);
          if (data.image_link) localStorage.setItem('backgroundPicture', data.image_link);
          applyStoredBrandingColors();
          window.dispatchEvent(new Event('branding-changed'));
        }
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const accept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await apiRequest('POST', `/bravos/invite/${token}/accept`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
      });
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      navigate(data.redirect || '/member-home');
    } catch (err) {
      if (err.message === 'accountExistsLoginFirst') {
        setLoginFirst(true);
      } else {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const valid = info && info.valid;
  const subtitle = valid
    ? [info.event_date ? formatDate(info.event_date) : null, info.location_name]
      .filter(Boolean).join(' · ')
    : '';

  return (
    <AuthShell
      title={valid ? info.event_name : t('inviteTitle')}
      subtitle={subtitle}
      hideBrand={!!(info && info.logo_url)}
    >
      {info && info.logo_url && (
        <div className="text-center mb-3">
          <img src={info.logo_url} alt={info.company_name || ''}
               style={{ maxHeight: '56px', maxWidth: '180px', objectFit: 'contain' }} />
        </div>
      )}
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : !valid ? (
        <div className="auth__card">
          <p>{t('invalidLink')}</p>
        </div>
      ) : !info.has_seat ? (
        <div className="auth__card">
          <p>{t('inviteNoSeats')}</p>
        </div>
      ) : loginFirst ? (
        <div className="auth__card">
          <p className="auth__hint">
            <Icon name="user-check" /> {t('inviteLoginFirst')}
          </p>
          <button
            type="button"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            onClick={() => navigate('/login')}
          >
            <Icon name="sign-in-alt" /> {t('login')}
          </button>
        </div>
      ) : (
        <form className="auth__card" onSubmit={accept}>
          {info.company_name && (
            <p className="auth__hint" style={{ fontWeight: 600 }}>
              <Icon name="user-check" /> {t('invitedByCompany', { company: info.company_name })}
            </p>
          )}

          <div className="auth__row">
            <div className="auth__field">
              <label className="auth__label">{t('firstName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('enterFirstName')}
                  autoComplete="given-name"
                  required
                />
              </div>
            </div>
            <div className="auth__field">
              <label className="auth__label">{t('lastName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('enterLastName')}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
          </div>

          <div className="auth__field">
            <label className="auth__label">{t('email')}</label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('enterEmail')}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="auth__field">
            <label className="auth__label">{t('mobileNumber')}</label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder={t('enterMobile')}
                autoComplete="tel"
                required
              />
            </div>
            <WhatsAppNotice />
          </div>

          <button
            type="submit"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            disabled={submitting}
          >
            {submitting ? <span className="auth__spinner" /> : <Icon name="user-add" />}
            {t('acceptInvite')}
          </button>

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}

      {/* Event explanation badge (learn more about the event / brand). */}
      {valid && info.commercial_url && (
        <EventCta eventName={info.event_name} commercialUrl={info.commercial_url} flush />
      )}
    </AuthShell>
  );
};

export default InviteAccept;
